import useSWR from 'swr';
import { getApprovalTypeUrl } from '@/constant/approval';
import type {
  ApprovalTypeDetail,
  ApprovalTypeReferrer,
} from '@/types/approval';
import { getNestedDataFetcher } from '@/utils/apis';

/**
 * 품의 양식(code) 메타 단건 조회. code falsy 시 호출 skip (SWR key null).
 * 응답: `{ data: ApprovalTypeDetail }`. 기존 분리 fixed-co-approvers/fixed-referrers 호출을 통합.
 */
const useApprovalType = (code?: number | null) => {
  const key = typeof code === 'number' ? getApprovalTypeUrl(code) : null;
  return useSWR<ApprovalTypeDetail>(key, getNestedDataFetcher);
};

export default useApprovalType;

/** 미도착 시 매번 새 배열을 만들면 파생 useMemo 가 계속 무효화된다 — 고정 참조로 공유 */
const EMPTY_MEMBERS: ApprovalTypeReferrer[] = [];

/** 양식 메타의 고정 결재자(순차 결재 단계) 추출. data 미도착 시 빈 배열. */
export const useApprovalTypeFixedApprovers = (
  code?: number | null,
): ApprovalTypeReferrer[] => {
  const { data } = useApprovalType(code);
  return data?.fixedApprovers ?? EMPTY_MEMBERS;
};

/**
 * 양식 메타 중 고정 협조결재자만 추출.
 * 기존 분리 훅 호환을 위한 derived selector. data 미도착 시 빈 배열.
 */
export const useApprovalTypeFixedCoApprovers = (
  code?: number | null,
): ApprovalTypeReferrer[] => {
  const { data } = useApprovalType(code);
  return data?.fixedCoApprovers ?? EMPTY_MEMBERS;
};

/**
 * 양식 메타의 고정 참조인 추출.
 * 법인 매칭·퇴사자 제외는 BE(v2)가 요청자 기준으로 처리해서 내려주므로 여기서 더 걸러내지 않는다.
 */
export const useApprovalTypeFixedReferrers = (
  code?: number | null,
): ApprovalTypeReferrer[] => {
  const { data } = useApprovalType(code);
  return data?.fixedReferrers ?? EMPTY_MEMBERS;
};
